<script setup lang="ts">
import { useFocusTrap } from '@vueuse/integrations/useFocusTrap'
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock'

const { acceptAll, rejectAll, readLocalPrefs } = usePrivacy()

const open = ref(true)

const openPrivacyControl = ref(false)

watch(readLocalPrefs, (localPrefs) => {
  if (localPrefs)
    open.value = false
  else
    open.value = true
}, {
  immediate: true,
})

watch(openPrivacyControl, (newState) => {
  if (newState === true)
    open.value = false
})

// ==================
// Privacy Modal
// ==================

const modalEl = ref<HTMLElement>()

const { activate: startTrap, deactivate: stopTrap } = useFocusTrap(modalEl, {
  initialFocus: false,
})

const onModal = {
  afterEnter: (el: HTMLElement) => {
    disableBodyScroll(el)
    startTrap()
  },
  beforeLeave: (el: HTMLElement) => {
    stopTrap()
    enableBodyScroll(el)
  },
}

function closeModal() {
  openPrivacyControl.value = false
}
</script>

<template>
  <ClientOnly>
    <Transition
      name="consentbanner"
      appear
    >
      <aside
        v-if="open"
        class="py-4 border-t border-t-icssilver bg-icssilver-50 w-screen bottom-0 fixed z-9999 elevation-5 media-[(pointer:fine)]:pb-6 dark:border-t-icsjet dark:bg-icsjet-900"
      >
        <BlockContent class="flex flex-wrap gap-y-4 items-center justify-between">
          <div>
            <h6 class="text-lg heading">
              Privacy Settings
            </h6>
            <!-- eslint-disable-next-line -->
            <p class="text-xs">This website uses cookies to save your preferences and other crucial functions. With your consent, it may also use non-essential cookies to analyze website traffic. By clicking "Accept," you agree to our website's cookie use as described in our <NuxtLink class="link" to="https://www.trexon.com/privacy-policy" target="_blank">Privacy Policy</NuxtLink>. You can change your privacy settings at any time by clicking "Privacy Settings" in the website footer.
            </p>
          </div>
          <div class="flex flex-wrap gap-x-4">
            <AButton
              color="blue"
              size="small"
              icon="i-carbon-thumbs-up"
              @click="acceptAll()"
            >
              Accept
            </AButton>
            <AButton
              size="small"
              color="red"
              icon="i-carbon-thumbs-down"
              @click="rejectAll()"
            >
              Decline
            </AButton>

            <AButton
              size="small"
              icon="i-carbon-settings"
              @click="openPrivacyControl = true"
            >
              Customize
            </AButton>
          </div>
        </BlockContent>
      </aside>
    </Transition>
  </ClientOnly>
  <ClientOnly>
    <Teleport to="#teleports">
      <Transition
        name="privacymodal"
        v-on="onModal"
      >
        <div
          v-if="openPrivacyControl"
          ref="modalEl"
          class="flex items-center inset-0 justify-center fixed z-9999"
        >
          <div
            class="bg-black/50 absolute-inset"
            @click="closeModal"
          />
          <div class="mx-4 rounded-sm bg-white max-w-lg w-full shadow-xl relative dark:bg-icsjet-800">
            <button
              class="ics-4ilrek"
              aria-label="Close privacy settings"
              @click="closeModal"
            >
              <div class="i-carbon-close size-5" />
            </button>
            <PrivacyControl @saved="closeModal" />
          </div>
        </div>
      </Transition>
    </Teleport>
  </ClientOnly>
</template>

<style>
.consentbanner-leave-active,
.consentbanner-enter-active {
  transition-property: transform;
  transition-duration: 150ms;
  transition-timing-function: cubic-bezier(0, 0, 0.2, 1); /* ease-out */
}
.consentbanner-enter-from,
.consentbanner-leave-to {
  transform: translateY(100%);
}
.consentbanner-enter-to,
.consentbanner-leave-from {
  transform: translateY(0);
}
.privacymodal-enter-active,
.privacymodal-leave-active {
  transition-property: opacity;
  transition-duration: 150ms;
  transition-timing-function: cubic-bezier(0, 0, 0.2, 1);
}
.privacymodal-enter-from,
.privacymodal-leave-to {
  opacity: 0;
}
.privacymodal-enter-to,
.privacymodal-leave-from {
  opacity: 1;
}
</style>
